import React, { useEffect, useRef } from 'react';
import { MdArrowBack, MdMic, MdMicOff, MdVolumeOff, MdClose } from 'react-icons/md';

/**
 * Full-screen voice surface — ChatGPT-style. Minimal UI, big animated orb,
 * one tap to start/stop. The underlying conversation pipeline lives in
 * ChatScreen (handleSend → callBackendAPI → speak); this component is
 * purely a different way to drive it. When the user exits, the chat
 * history they generated by voice is already in ChatScreen's state.
 *
 * Props:
 *   onExit              — close the surface, return to normal chat.
 *   onStop              — cancel any in-flight TTS / mic.
 *   isListening, isSpeaking, isProcessing — derived render state.
 *   transcript          — live partial transcript (rendered as a hint).
 *   toggleListening     — start/stop the mic.
 *   cancelSpeech        — cancel TTS playback.
 *   voiceSupported      — false on browsers without Web Speech API.
 *   autoRestartOnSpeakEnd — bool; when true, mic re-opens after the bot
 *                            finishes speaking (continuous-conversation feel).
 */
export default function ConversationMode({
  onExit,
  isListening,
  isSpeaking,
  isProcessing,
  transcript,
  toggleListening,
  cancelSpeech,
  voiceSupported,
  autoRestartOnSpeakEnd = true,
}) {
  // Compute a single string state for the UI: idle / listening / thinking / speaking.
  const state = isListening ? 'listening'
              : isProcessing ? 'thinking'
              : isSpeaking ? 'speaking'
              : 'idle';

  // Auto-flip the mic back on after the bot finishes speaking — gives the
  // ChatGPT-style continuous turn-taking feel. Skipped on the very first
  // mount so the user has to tap once to start the first turn.
  const lastSpokeRef = useRef(false);
  const startedOnceRef = useRef(false);
  useEffect(() => {
    if (!autoRestartOnSpeakEnd) return;
    if (lastSpokeRef.current && !isSpeaking && !isListening && !isProcessing && startedOnceRef.current) {
      const t = setTimeout(() => toggleListening(), 350);
      return () => clearTimeout(t);
    }
    lastSpokeRef.current = isSpeaking;
  }, [isSpeaking, isListening, isProcessing, autoRestartOnSpeakEnd, toggleListening]);
  useEffect(() => { if (isListening) startedOnceRef.current = true; }, [isListening]);

  // Stop any in-flight audio on exit so the user isn't followed out of the
  // surface by a half-spoken reply.
  const handleExit = () => {
    if (isSpeaking) cancelSpeech();
    if (isListening) toggleListening();
    onExit();
  };

  const stateLabel = {
    idle:      'Tap to speak',
    listening: 'Listening…',
    thinking:  'Thinking…',
    speaking:  'Speaking…',
  }[state];

  const bigButtonHandler = () => {
    if (state === 'speaking') { cancelSpeech(); return; }
    if (state === 'thinking') return; // wait it out
    toggleListening();
  };

  return (
    <div className={`conv-mode is-${state}`} role="dialog" aria-label="Voice conversation">
      <header className="conv-mode-header">
        <button className="conv-mode-back" onClick={handleExit} aria-label="Exit conversation mode">
          <MdArrowBack size={22} />
        </button>
        <span className="conv-mode-title">Conversation</span>
        <span className="conv-mode-spacer" />
      </header>

      <div className="conv-mode-stage">
        <button
          type="button"
          className={`conv-mode-orb is-${state}`}
          onClick={bigButtonHandler}
          aria-label={stateLabel}
          disabled={!voiceSupported && state !== 'speaking'}
        >
          <span className="conv-mode-orb-pulse" />
          <span className="conv-mode-orb-core" />
        </button>

        <div className="conv-mode-status" aria-live="polite">{stateLabel}</div>

        {(isListening && transcript) && (
          <div className="conv-mode-transcript">"{transcript}"</div>
        )}

        {!voiceSupported && (
          <div className="conv-mode-hint">
            Voice isn't supported in this browser. Switch to text chat to continue.
          </div>
        )}
      </div>

      <div className="conv-mode-controls">
        {state === 'speaking' && (
          <button className="conv-mode-control" onClick={cancelSpeech} aria-label="Stop speaking">
            <MdVolumeOff size={20} /> <span>Stop</span>
          </button>
        )}
        {state === 'listening' && (
          <button className="conv-mode-control" onClick={toggleListening} aria-label="Stop listening">
            <MdMicOff size={20} /> <span>Stop</span>
          </button>
        )}
        {state === 'idle' && voiceSupported && (
          <button className="conv-mode-control conv-mode-control--primary" onClick={toggleListening} aria-label="Start speaking">
            <MdMic size={20} /> <span>Speak</span>
          </button>
        )}
        <button className="conv-mode-control" onClick={handleExit} aria-label="Close conversation mode">
          <MdClose size={20} /> <span>Close</span>
        </button>
      </div>
    </div>
  );
}
